/**
 * Shared contributor navigation — MY WORK · OUTPUTS · WORKSPACE
 * Matches the focused participant shell from sessions-redesign-canvas.
 */

import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useRoleMenu } from '../../hooks/prioritization/useContributorExperience';
import { useParticipantSessions } from '../../features/sessions-participant/hooks/useParticipantSessions';
import { C, SIDEBAR_W } from '../../features/sessions-participant/theme/participantTokens';
import { ROLE_MENU_SETTINGS_HREF, type RoleMenuIcon } from '../../lib/contributorNavProfiles';
import { WRISTBAND_HREF } from '../../lib/areaMenus';
import { ProductModeSwitcher } from './ProductModeSwitcher';

// ── Icons ────────────────────────────────────────────────────────────────────

const Icons: Record<RoleMenuIcon | 'settings' | 'game', ReactNode> = {
  game: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M2 4h6a4 4 0 014 4v13a3 3 0 00-3-3H2z" /><path d="M22 4h-6a4 4 0 00-4 4v13a3 3 0 013-3h7z" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h.01a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51h.01a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.01a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
  sessions: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  tasks: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
    </svg>
  ),
  exchange: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 014-4h14" />
      <path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 01-4 4H3" />
    </svg>
  ),
  results: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  reports: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  ),
  items: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 2l9 4.9V17L12 22l-9-4.9V7L12 2z" />
    </svg>
  ),
  submit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 5v14" /><path d="M5 12h14" />
    </svg>
  ),
  cycles: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
    </svg>
  ),
  plan: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2z" /><path d="M9 4v14" /><path d="M15 6v14" />
    </svg>
  ),
  apps: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
    </svg>
  ),
};

const sectionLabel: React.CSSProperties = {
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '0.6875rem',
  letterSpacing: '0.16em',
  color: C.textSec,
  textTransform: 'uppercase',
  padding: '14px 16px 6px',
  fontWeight: 600,
};

// ── Nav item ─────────────────────────────────────────────────────────────────

interface NavItemProps {
  label: string;
  href: string;
  icon: ReactNode;
  count?: number;
  forceActive?: boolean;
}

function NavItem({ label, href, icon, count, forceActive }: NavItemProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const qIdx = href.indexOf('?');
  const hrefPath = qIdx === -1 ? href : href.slice(0, qIdx);
  const hrefSearch = qIdx === -1 ? '' : href.slice(qIdx);
  const active = forceActive ?? (
    (location.pathname === hrefPath || location.pathname.startsWith(`${hrefPath}/`))
    && (hrefSearch === '' || location.search === hrefSearch)
  );

  return (
    <button
      type="button"
      onClick={() => navigate(hrefSearch ? { pathname: hrefPath, search: hrefSearch } : hrefPath)}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '9px 16px',
        gap: 12,
        border: 'none',
        borderLeft: active ? `3px solid ${C.slate}` : '3px solid transparent',
        background: active ? C.activeTint : 'none',
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <span style={{ width: 16, height: 16, color: active ? C.textPri : C.textSec, flexShrink: 0, display: 'flex' }}>
        {icon}
      </span>
      <span style={{
        fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
        fontSize: '0.875rem',
        fontWeight: active ? 600 : 500,
        color: active ? C.textPri : C.textSec,
        flex: 1,
        lineHeight: 1.35,
      }}>
        {label}
      </span>
      {count !== undefined && count > 0 && (
        <span style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: C.coral,
          minWidth: 16,
          textAlign: 'right',
        }}>
          {count}
        </span>
      )}
    </button>
  );
}

// ── Sidebar ──────────────────────────────────────────────────────────────────

interface ContributorSidebarProps {
  /** When embedded in ParticipantShell during a session, pin Sessions as active */
  sessionsActive?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export function ContributorSidebar({ sessionsActive, className, style }: ContributorSidebarProps) {
  const userName = useUserStore((s) => s.userName);
  // One list for desktop and phone: the role-based menu (union of the person's
  // featured roles, or all their roles), each link only when they may use it.
  const menu = useRoleMenu();
  const { needsInputCount } = useParticipantSessions();

  const userInitials = userName
    ? userName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'ME';

  const sectionBlocks = menu.sections.map((section, index) => (
    <div key={section.id}>
      <div style={index === 0 ? sectionLabel : { ...sectionLabel, marginTop: 8 }}>{section.label}</div>
      {section.links.map((link) => (
        <NavItem
          key={link.href}
          label={link.label}
          href={link.href}
          icon={Icons[link.icon]}
          count={link.showsSessionCount && needsInputCount > 0 ? needsInputCount : undefined}
          forceActive={link.showsSessionCount && sessionsActive ? true : undefined}
        />
      ))}
    </div>
  ));

  return (
    <aside
      className={className}
      style={{
        width: SIDEBAR_W,
        background: C.bgRaised,
        borderRight: `1px solid ${C.rule}`,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...style,
      }}
    >
      {menu.showProductSwitch && <ProductModeSwitcher />}

      <div style={{ padding: '16px 16px 12px', borderBottom: `1px solid ${C.rule}` }}>
        <div style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.6875rem',
          letterSpacing: '0.16em',
          color: C.textSec,
          textTransform: 'uppercase',
          fontWeight: 600,
        }}>
          Workspace
        </div>
      </div>

      <div style={{ overflowY: 'auto', flex: 1, padding: '6px 0' }}>
        {sectionBlocks}
      </div>

      {/* Every user owns their personal feature toggles — the contributor
          shell must not orphan them from Settings (owner, 2026-07-09). */}
      <div style={{ borderTop: `1px solid ${C.rule}`, padding: '6px 0' }}>
        {/* The Game: the wristband, the words the record uses. For everyone, like Settings. */}
        <NavItem label="The Game" href={WRISTBAND_HREF} icon={Icons.game} />
        <NavItem label="Settings" href={ROLE_MENU_SETTINGS_HREF} icon={Icons.settings} />
      </div>

      <div style={{
        borderTop: `1px solid ${C.rule}`,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
      }}>
        <div style={{
          width: 30,
          height: 30,
          borderRadius: '50%',
          background: C.activeTint,
          border: `1px solid ${C.slate}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.625rem',
          fontWeight: 700,
          color: C.textPri,
          flexShrink: 0,
        }}>
          {userInitials}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
            fontSize: '0.875rem',
            color: C.textPri,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {userName || 'User'}
          </div>
        </div>
      </div>
    </aside>
  );
}

/** Top bar shared by contributor layout and participant shell */
export function ContributorTopBar({
  pageTitle,
  trailing,
}: {
  pageTitle: string;
  trailing?: ReactNode;
}) {
  return (
    <nav style={{
      height: 52,
      background: C.bgRaised,
      borderBottom: `1px solid ${C.rule}`,
      display: 'flex',
      alignItems: 'center',
      padding: '0 20px',
      flexShrink: 0,
      zIndex: 10,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: '0.6875rem',
        letterSpacing: '0.16em',
        color: C.textSec,
        textTransform: 'uppercase',
        fontWeight: 600,
      }}>
        <svg viewBox="0 0 142.85 94.47" xmlns="http://www.w3.org/2000/svg" aria-hidden style={{ height: 20 }}>
          <rect fill="#bdbec5" x="0" y="82.27" width="142.85" height="12.2" />
          <rect fill="#aaadb9" x="15.02" y="65.76" width="112.82" height="12.2" />
          <rect fill="#9399ad" x="28.15" y="49.25" width="86.54" height="12.2" />
          <rect fill="#6a718b" x="39.42" y="32.73" width="64.02" height="12.2" />
          <rect fill="#4f597a" x="48.8" y="16.22" width="45.25" height="12.2" />
          <rect fill="#424c68" x="55.4" y="0" width="32.34" height="11.61" />
        </svg>
        <strong style={{ color: C.textPri, fontWeight: 600 }}>PRIORITIES.AI</strong>
      </div>
      <div style={{ width: 1, height: 20, background: C.ruleBright, margin: '0 16px' }} />
      <div style={{ fontFamily: 'Spectral, Georgia, serif', fontSize: '1.125rem', fontWeight: 600, color: C.textPri }}>
        {pageTitle}
      </div>
      {trailing && (
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {trailing}
        </div>
      )}
    </nav>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function contributorPageTitle(pathname: string): string {
  if (pathname.includes('/cycles')) return 'Cycles';
  if (pathname.includes('/tracks')) return 'Tracks';
  if (pathname.includes('/priorities')) return 'Item Pools';
  if (pathname.includes('/relationships')) return 'Relationships';
  if (pathname.includes('/settings/workflows')) return 'Workflows';
  if (pathname.includes('/scoring')) return 'Scoring';
  if (pathname.includes('/sessions')) return 'Sessions';
  if (pathname.includes('/my-work') || pathname.includes('/my-tasks')) return 'My work';
  if (pathname.includes('/catchball/executive-inbox')) return 'Executive Inbox';
  if (pathname.includes('/catchball')) return 'Cascade';
  if (pathname.includes('/reports')) return 'Reports';
  if (pathname.includes('/scoreboard')) return 'Results';
  if (pathname.includes('/items')) return 'Items';
  if (pathname.includes('/intake')) return 'Submit an idea';
  if (pathname.includes('/prioritization/apps')) return 'Apps';
  if (pathname.includes(WRISTBAND_HREF)) return 'The Game';
  return 'Workspace';
}
